import {
  CheckCircleOutlined,
  MoreOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Col, Dropdown, Menu, Modal, Table, Tooltip } from 'antd';
import { FC, useContext, useEffect, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { fetchUserGroups } from '../../api/groups';
import { GroupDetails, UserWithGroupsDetails } from '../../constants/types';
import { LoggedInUserContext } from '../../context/loggedInUser';
import theme from '../../styles/theme';
import { getParentOrigin } from '../../utils/getKeycloakConfig';
import {
  ActionLink,
  Actions,
  Email,
  GroupsList,
  GroupsListItem,
  GroupsListModal,
  Name,
} from './style';

interface Props {
  loading: boolean;
  enabledUsersShown: boolean;
  users: UserWithGroupsDetails[];
  highlightFirstItem?: boolean;
  onDeactivate: Function;
  onReactivate: Function;
}
interface UsersTableItem {
  id: string;
  key: number;
  name: string;
  email: string;
  groups: GroupDetails[];
  status: string;
}

interface Confirmation {
  showModal: boolean;
  content: any;
  onOk: Function;
  onCancel: Function;
}

const UsersTable: FC<Props> = props => {
  const {
    loading,
    users,
    highlightFirstItem,
    onDeactivate,
    onReactivate,
    enabledUsersShown,
  } = props;
  const history = useHistory();
  const intl = useIntl();
  const parentOrigin = getParentOrigin();
  const loggedInUser = useContext<any>(LoggedInUserContext);
  const [actionConfirmationDefinition, setActionConfirmationDefinition] =
    useState<Confirmation>({
      showModal: false,
      content: null,
      onOk: () => {
        /* This is intentionally empty as it's a placeholder that gets replaced later */
      },
      onCancel: () => {
        /* This is intentionally empty as it's a placeholder that gets replaced later */
      },
    });
  const [selectedUserGroupsList, setSelectedUserGroupsList] = useState<any>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isGroupListModalOpen, setIsGroupListModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (highlightFirstItem) {
      setCurrentPage(1);
    }
  }, [highlightFirstItem]);

  const onViewUsersGroups = async (records: UsersTableItem) => {
    const groups = await fetchUserGroups(records.id);

    setSelectedUserGroupsList({
      user: `${users[records.key].firstName} ${users[records.key].lastName}`,
      groups,
    });
    setIsGroupListModalOpen(true);
  };

  const getTableData = () => {
    return users.map((user, index) => ({
      id: user.id,
      key: index,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      groups: user.groups,
      status: user.enabled ? 'Enabled' : 'Disabled',
    })) as UsersTableItem[];
  };

  const nameColumn = [
    {
      title: intl.formatMessage({
        id: 'User.colum.name',
      }),
      dataIndex: 'name',
      key: 'name',
      sorter: (a: UsersTableItem, b: UsersTableItem) =>
        a.name.localeCompare(b.name),
      render(props: string) {
        return <Name>{props}</Name>;
      },
    },
  ];

  const activeUserColumnHeader = [
    ...nameColumn,
    {
      title: intl.formatMessage({
        id: 'User.colum.email',
      }),
      dataIndex: 'key',
      key: 'email',
      render(userIndex: number) {
        return (
          <Email verified={users[userIndex].emailVerified}>
            <Tooltip
              title={`${intl.formatMessage({
                id: users[userIndex].emailVerified
                  ? 'User.colum.verified'
                  : 'User.colum.unVerified',
              })}`}
              placement="right"
            >
              <CheckCircleOutlined />
            </Tooltip>
            <span
              className={`${
                !users[userIndex].emailVerified ? 'ml-26' : 'ml-8'
              }`}
            >
              {users[userIndex].email}
            </span>
          </Email>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'User.colum.userGroups',
      }),
      dataIndex: '',
      key: 'groups',
      width: 200,
      render: (records: UsersTableItem) => (
        <ActionLink
          className="link"
          onClick={() => onViewUsersGroups(records)}
          data-testid={`usergroups-view-${records.id}`}
        >
          <FormattedMessage id="User.colum.viewUserGroups" />
        </ActionLink>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'User.colum.action',
      }),
      dataIndex: 'key',
      key: 'actions',
      width: 95,
      align: 'center',
      fixed: 'right',
      className: 'table-action-column',
      render(userIndex: number) {
        return (
          <>
            <Dropdown
              overlay={renderUserActions(users[userIndex])}
              trigger={['click']}
              placement="bottomRight"
            >
              <MoreOutlined
                className="three-dot-icon"
                data-testid={`user-options-${userIndex}`}
              />
            </Dropdown>
          </>
        );
      },
    },
  ];

  const deactiveUserColumnHeader = [
    ...nameColumn,
    {
      title: intl.formatMessage({
        id: 'User.colum.email',
      }),
      dataIndex: 'key',
      key: 'email',
      render(userIndex: number) {
        return (
          <Email verified={users[userIndex].emailVerified}>
            <Tooltip
              title={`${intl.formatMessage({
                id: users[userIndex].emailVerified
                  ? 'User.colum.verified'
                  : 'User.colum.unVerified',
              })}`}
              placement="right"
            >
              <CheckCircleOutlined />
            </Tooltip>
            <span
              className={`${
                !users[userIndex].emailVerified ? 'ml-26' : 'ml-8'
              }`}
            >
              {users[userIndex].email}
            </span>
          </Email>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'User.colum.action',
      }),
      dataIndex: 'key',
      key: 'actions',
      width: '10%',
      align: 'center',
      fixed: 'right',
      className: 'table-action-column',
      render(userIndex: number) {
        return (
          <Actions>
            <ActionLink
              className="link"
              data-testid={`user-activate-${userIndex}`}
              onClick={() => onReactivateAttempt(users[userIndex])}
            >
              <FormattedMessage id="User.colum.activate" />
            </ActionLink>
          </Actions>
        );
      },
    },
  ];

  // Redirect to edit user page of Spark UI if parentUrl exist
  // Normal history push if not
  const redirectToEditUser = (userId: string | undefined) => {
    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: `/admin/users/edit/${userId}`,
        },
        parentOrigin,
      );
    } else {
      history.push(`/users/edit/${userId}`);
    }
  };

  const renderUserActions = (userDetails: UserWithGroupsDetails) => (
    <Menu>
      <Menu.Item
        key="user-edit"
        data-testid="user-edit"
        onClick={() => redirectToEditUser(userDetails?.id)}
      >
        <FormattedMessage id="User.colum.editUser" />
      </Menu.Item>
      {loggedInUser.preferred_username !== userDetails.username &&
        loggedInUser.username !== userDetails.username && (
          <>
            <Menu.Item
              key="user-deactivate"
              data-testid="user-deactivate"
              onClick={() => onDeactivateAttempt(userDetails)}
            >
              <FormattedMessage id="User.colum.deactivateUser" />
            </Menu.Item>
          </>
        )}
    </Menu>
  );

  const resetActionConfirmationDefinition = () => {
    setActionConfirmationDefinition({
      showModal: false,
      content: null,
      onOk: () => {
        /* This is intentionally empty as it's a placeholder that gets replaced later */
      },
      onCancel: () => {
        /* This is intentionally empty as it's a placeholder that gets replaced later */
      },
    });
  };

  const onDeactivateAttempt = (userDetails: UserWithGroupsDetails) => {
    setActionConfirmationDefinition({
      showModal: true,
      content: (
        <p>
          <FormattedHTMLMessage
            id="User.delete.warning"
            values={{ group: userDetails.username }}
          />
        </p>
      ),
      onOk: () => {
        resetActionConfirmationDefinition();
        onDeactivate(userDetails.id, userDetails.username);
      },
      onCancel: () => resetActionConfirmationDefinition(),
    });
  };

  const onReactivateAttempt = (userDetails: UserWithGroupsDetails) => {
    setActionConfirmationDefinition({
      showModal: true,
      content: (
        <p>
          <FormattedHTMLMessage
            id="User.modal.userActivate"
            values={{ user: userDetails.username }}
          />
        </p>
      ),
      onOk: () => {
        resetActionConfirmationDefinition();
        onReactivate(userDetails.id);
      },
      onCancel: () => resetActionConfirmationDefinition(),
    });
  };

  return (
    <>
      <Table
        loading={loading}
        showSorterTooltip={false}
        dataSource={getTableData()}
        columns={
          enabledUsersShown
            ? (activeUserColumnHeader as any)
            : (deactiveUserColumnHeader as any)
        }
        tableLayout="fixed"
        bordered={false}
        size="middle"
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          current: currentPage,
          onChange: pageNumber => setCurrentPage(pageNumber),
          size: 'default',
        }}
        className={`${enabledUsersShown ? 'last-2-column-merge' : ''}`}
      />

      <Modal
        className="userlistmodal"
        width={500}
        data-testid="user-confirmation-modal"
        closable={false}
        visible={actionConfirmationDefinition.showModal}
        onCancel={() => actionConfirmationDefinition.onCancel()}
        cancelButtonProps={{
          type: 'default',
          shape: 'round',
          style: {
            width: '30%',
          },
        }}
        onOk={() => actionConfirmationDefinition.onOk()}
        okText={<FormattedMessage id="Global.confirm" />}
        okButtonProps={{
          shape: 'round',
          style: {
            width: '30%',
          },
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <WarningOutlined
            style={{ color: theme.palette.error, fontSize: '48px' }}
          />
          {actionConfirmationDefinition.content}
        </div>
      </Modal>
      <GroupsListModal
        visible={isGroupListModalOpen}
        closable={false}
        footer={null}
        data-testid="usergroups-list"
      >
        <h3>
          <FormattedMessage id="ApiKeys.modal.userGroupsList" />
        </h3>

        <p>
          <b style={{ marginRight: '10px' }}>
            <FormattedMessage id="User.modal.lable.user" />
          </b>
          {selectedUserGroupsList.user}
        </p>

        <GroupsList bordered>
          {selectedUserGroupsList.groups?.length > 0 &&
            selectedUserGroupsList.groups?.map(
              (group: GroupDetails, index: number) => (
                <GroupsListItem key={index}>{group.name}</GroupsListItem>
              ),
            )}
          {selectedUserGroupsList.groups?.length === 0 && (
            <GroupsListItem>There are no groups yet.</GroupsListItem>
          )}
        </GroupsList>

        <Col style={{ marginTop: '30px' }} span={10} offset={7}>
          <Button
            type="primary"
            shape="round"
            block
            data-testid="usergroups-list-close"
            onClick={() => {
              setSelectedUserGroupsList({});
              setIsGroupListModalOpen(false);
            }}
          >
            <FormattedMessage id="Global.close" />
          </Button>
        </Col>
      </GroupsListModal>
    </>
  );
};

export default UsersTable;
